import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext, useWatch } from 'react-hook-form'
import { useVoices, useVoice } from '@/hooks/api'
import {
  LanguageSelector,
  SortableLanguagesInput,
  VoiceSelectionCard,
  VoiceSelectionDrawer,
  GreetingSection,
  LexiconEditDialog,
} from '../index'
import type { Voice } from '@/types/assistant'

/**
 * Personality Tab - Voice, language, and greeting settings
 *
 * Includes:
 * - Primary language selector with flag emoji
 * - Additional languages with drag-to-reorder
 * - Voice selection card with audio preview
 * - Pronunciation/Lexicon edit dialog
 * - Greeting message section (dynamic/personalized toggles)
 * - Advanced settings (persona name)
 */
export function PersonalityTab() {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext()
  const [isVoiceDrawerOpen, setIsVoiceDrawerOpen] = useState(false)
  const [playableGreeting, setPlayableGreeting] = useState('')
  const handlePlayableGreetingChange = useCallback((text: string) => setPlayableGreeting(text), [])

  // Watch form values
  const voiceId = useWatch({ control, name: 'voice_id' }) as number | undefined
  const defaultLanguage = useWatch({ control, name: 'default_language' }) as string
  const additionalLanguages = useWatch({ control, name: 'additional_languages' }) as string[]
  const greeting = useWatch({ control, name: 'greeting' }) as string
  const assistantId = useWatch({ control, name: 'id' }) as number

  // Fetch voices to find selected voice
  const { data: voices = [] } = useVoices()
  const selectedVoice = useVoice(voiceId, voices)

  // Handle voice selection
  const handleVoiceSelect = (voice: Voice) => {
    setValue('voice_id', voice.id, { shouldDirty: true })
    setIsVoiceDrawerOpen(false)
  }

  // Handle primary language change
  const handleLanguageChange = (value: string) => {
    setValue('default_language', value, { shouldDirty: true })
  }

  // Handle additional languages change
  const handleAdditionalLanguagesChange = (value: string[]) => {
    setValue('additional_languages', value, { shouldDirty: true })
  }

  return (
    <div className="space-y-10">
      {/* Voice and Language Settings Section */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('resources.assistants.personality.voice_and_language_settings')}
        </h2>

        <div className="mt-6 rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
          <div className="space-y-8">
            {/* Primary Language */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('resources.assistants.fields.default_language')}
              </label>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('resources.assistants.personality.primary_language_description', { defaultValue: 'This is the language that will be spoken when a customer reaches your business.' })}
              </p>
              <div className="mt-3 max-w-md">
                <LanguageSelector
                  value={defaultLanguage}
                  onChange={handleLanguageChange}
                  placeholder={t('resources.assistants.personality.select_primary_language', { defaultValue: 'Select primary language' })}
                />
              </div>
            </div>

            {/* Additional Languages */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('resources.assistants.fields.additional_languages')}
              </label>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('resources.assistants.personality.additional_languages_description', { defaultValue: 'Callers can select their preferred language by pressing a number on the keypad.' })}
              </p>
              <div className="mt-3">
                <SortableLanguagesInput
                  value={additionalLanguages || []}
                  onChange={handleAdditionalLanguagesChange}
                  excludeLanguages={defaultLanguage ? [defaultLanguage] : []}
                />
              </div>
            </div>

            {/* Voice Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('resources.assistants.fields.voice')}
              </label>
              <div className="mt-3">
                <VoiceSelectionCard
                  selectedVoice={selectedVoice}
                  onClick={() => setIsVoiceDrawerOpen(true)}
                  greeting={playableGreeting || greeting}
                  defaultLanguage={defaultLanguage}
                  assistantId={assistantId}
                />
              </div>
            </div>

            {/* Pronunciation / Lexicon */}
            <LexiconEditDialog />
          </div>
        </div>
      </section>

      {/* Greeting Message Section */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('resources.assistants.personality.greeting_message')}
        </h2>

        <div className="mt-6 rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
          <GreetingSection assistantId={assistantId} onPlayableGreetingChange={handlePlayableGreetingChange} />
        </div>
      </section>

      {/* Voice Selection Drawer */}
      <VoiceSelectionDrawer
        isOpen={isVoiceDrawerOpen}
        onClose={() => setIsVoiceDrawerOpen(false)}
        selectedVoiceId={voiceId}
        onSelect={handleVoiceSelect}
        greeting={playableGreeting || greeting}
        defaultLanguage={defaultLanguage}
        assistantId={assistantId}
      />
    </div>
  )
}

export default PersonalityTab
